import { useEffect } from 'react'
import { useToast } from '../../state-management/contexts/ToastContext'
import { AmountAdjustmentReason } from '../../utils/mf-execution/paymentAmount'
import { convertNumberToCurrency } from '../../utils/utils'
import { LocalToast } from '../ui-components/LocalToast'

const ALLOCATION_AMOUNT_ADJUSTMENT_TOAST_WIDTH = 'calc(100vw - 40px)'

type AllocationAmountAdjustmentCopy = 'investment' | 'withdrawal'

type AllocationAmountAdjustmentToastProps = {
  amountAdjustmentReason: AmountAdjustmentReason
  amount: number
  toastToBeShownById: string
  clearAmountAdjustmentReason: () => void
  copy?: AllocationAmountAdjustmentCopy
}

export function AllocationAmountAdjustmentToast({
  amountAdjustmentReason,
  amount,
  toastToBeShownById,
  clearAmountAdjustmentReason,
  copy = 'investment'
}: AllocationAmountAdjustmentToastProps) {
  useAllocationAmountAdjustmentToast({
    amountAdjustmentReason,
    amount,
    toastToBeShownById,
    clearAmountAdjustmentReason,
    copy
  })

  return (
    <LocalToast
      width={ALLOCATION_AMOUNT_ADJUSTMENT_TOAST_WIDTH}
      toastToBeShownById={toastToBeShownById}
    />
  )
}

export function useAllocationAmountAdjustmentToast({
  amountAdjustmentReason,
  amount,
  toastToBeShownById,
  clearAmountAdjustmentReason,
  copy = 'investment'
}: AllocationAmountAdjustmentToastProps) {
  const { showToast } = useToast()

  useEffect(() => {
    if (amountAdjustmentReason === AmountAdjustmentReason.NONE) return

    const formattedAmount = convertNumberToCurrency({ amount, casing: 'LOWER' })
    showToast({
      message: getAmountAdjustmentMessage(amountAdjustmentReason, formattedAmount, copy),
      toastToBeShownById,
      duration: 0,
      actionLabel: 'Got it',
      width: ALLOCATION_AMOUNT_ADJUSTMENT_TOAST_WIDTH
    })
    clearAmountAdjustmentReason()
  }, [amountAdjustmentReason, amount, toastToBeShownById, clearAmountAdjustmentReason, showToast, copy])
}

export function getAmountAdjustmentMessage(
  amountAdjustmentReason: AmountAdjustmentReason,
  formattedAmount: string,
  copy: AllocationAmountAdjustmentCopy
): string {
  if (amountAdjustmentReason === AmountAdjustmentReason.MIN) {
    if (copy === 'withdrawal') {
      return `Amount adjusted to fund's minimum withdrawal amount of ${formattedAmount}`
    }
    return `Amount adjusted to fund's minimum investment amount of ${formattedAmount}`
  }
  if (amountAdjustmentReason === AmountAdjustmentReason.MAX) {
    if (copy === 'withdrawal') {
      return `Amount adjusted to the maximum withdrawal amount of ${formattedAmount}`
    }
    return `Amount adjusted to the maximum investment amount of ${formattedAmount}`
  }
  return "Amount adjusted to the nearest whole number as per fund's guidelines"
}
